import { create } from "zustand";
import { jqXHR, ResponseError } from "../types/Api";
import {
  CalendarRule,
  CalendarRuleIsNonSchoolRule,
  Frequency,
  NonSchoolRule,
  RecurrenceID,
  RuleType,
  SchoolCalendarRule,
  Weekday,
  WEEKDAYS,
} from "../types/Calendar";
import Api from "../utils/Api";
import { useSessionStore } from "./SessionStore";
import posthog from "posthog-js";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export const calendarRulesURL = (): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v1/appliances/${device.id}/calendar-rules`;
};

export const calendarRulesBatchURL = (): string => {
  const device = useSessionStore.getState().getDevice();
  return `/cadm/v1/appliances/${device.id}/calendar-rules/batch-update`;
};

export enum RuleUpdateType {
  ONLY_THIS_INSTANCE = "false",
  THIS_AND_FUTURE_INSTANCES = "true",
}

export interface SaveOptions {
  recurrenceId?: RecurrenceID;
  updateType?: RuleUpdateType;
}

export interface BatchUpdateOptions {
  fixVersionConflict: boolean;
}

export interface DeleteOptions {
  recurrenceId?: RecurrenceID;
  deleteType?: RuleUpdateType;
}

export interface SchoolCalendarStore {
  schoolTimeRules: SchoolCalendarRule[];
  otherRules: NonSchoolRule[];
  timezone?: string;
  fetch: () => Promise<SchoolCalendarRule[] | undefined>;
  fetchNonSchoolDays: (year: number) => Promise<NonSchoolRule[]>;
  saveNonSchoolDays: (rule: CalendarRule, options?: SaveOptions) => Promise<CalendarRule>;
  saveBatchSchoolDays: (rules: CalendarRule[], options?: BatchUpdateOptions) => Promise<SchoolCalendarRule[]>;
  delete: (rule: NonSchoolRule, options?: DeleteOptions) => Promise<void>;
  reset: () => void;
}

export const useSchoolCalendarStore = create<SchoolCalendarStore>((set, get) => ({
  // type `school_time` rule
  schoolTimeRules: [],

  // type `other` rules
  otherRules: [],

  // appliance timezone
  timezone: undefined,

  fetch: async (): Promise<SchoolCalendarRule[] | undefined> => {
    let schoolTimeRules,
      timezone,
      statusCode = 200;

    const params = new URLSearchParams({ type: String(RuleType.SCHOOL_TIME) });

    // eslint-disable-next-line
    await Api.getAsync(`${calendarRulesURL()}?${params.toString()}`).then(
      (response: { rules: CalendarRule[]; applianceTimezone: string }) => {
        schoolTimeRules = response.rules.filter((r) => r.type === RuleType.SCHOOL_TIME);
        timezone = response.applianceTimezone;
      },
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (statusCode !== 200) {
      throw new ResponseError("Failed to fetch calendar rules", statusCode);
    }

    // Calendar rules doesn't exist, return without throwing
    if (!schoolTimeRules) {
      return undefined;
    }
    const parsed = parseCalendarRules(schoolTimeRules);
    set({ schoolTimeRules: parsed, timezone });

    return parsed;
  },

  fetchNonSchoolDays: async (year: number): Promise<NonSchoolRule[]> => {
    let otherRules: NonSchoolRule[] | undefined,
      timezone,
      statusCode = 200;

    const params = new URLSearchParams({ year: String(year), limit: "1000" });

    // eslint-disable-next-line
    await Api.getAsync(`${calendarRulesURL()}?${params.toString()}`).then(
      (response: { rules: CalendarRule[]; applianceTimezone: string }) => {
        otherRules = response.rules.filter(CalendarRuleIsNonSchoolRule);
        timezone = response.applianceTimezone;
      },
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (!otherRules || statusCode !== 200) {
      throw new ResponseError("Failed to fetch calendar rules", statusCode);
    }

    set({ otherRules, timezone });

    return otherRules;
  },

  saveNonSchoolDays: async (rule: CalendarRule, options?: SaveOptions): Promise<CalendarRule> => {
    const isNew = !rule.id;

    let saved: CalendarRule | undefined,
      statusCode = 200;

    const params = new URLSearchParams();
    if (options && options.recurrenceId) {
      params.append("recurrenceId", String(options.recurrenceId));
      if (options.updateType) {
        params.append("thisAndFuture", options.updateType);
      }
    }

    if (!isNew) {
      // eslint-disable-next-line
      await Api.putAsync(`${calendarRulesURL()}/${rule.id}?${params.toString()}`, rule).then(
        (response: CalendarRule) => (saved = response),
        (reason: jqXHR) => (statusCode = reason?.status)
      );
    } else {
      // eslint-disable-next-line
      await Api.postAsync(calendarRulesURL(), rule).then(
        (response: CalendarRule) => (saved = response),
        (reason: jqXHR) => (statusCode = reason?.status)
      );
    }

    if (!saved || statusCode !== 200) {
      throw new ResponseError("Failed to save calendar rule", statusCode);
    }

    if (CalendarRuleIsNonSchoolRule(saved)) {
      if (isNew) {
        set({ otherRules: [...get().otherRules, saved] });
      } else {
        const rules: NonSchoolRule[] = [];

        for (const r of get().otherRules) {
          if (r.id === saved.id) {
            rules.push(saved);
          } else {
            rules.push(r);
          }
        }

        set({ otherRules: rules });
      }
    }

    return saved;
  },

  saveBatchSchoolDays: async (rules: CalendarRule[], options?: BatchUpdateOptions): Promise<SchoolCalendarRule[]> => {
    let saved: SchoolCalendarRule[] | undefined;
    let statusCode = 200;
    const errorMessage = "Failed to batch update calendar rules";

    rules = rules
      .filter((rule) => rule.type === RuleType.SCHOOL_TIME)
      .map((rule) => (!rule.version ? { ...rule, version: 1 } : rule))
      .map((rule) => (!Boolean(rule.startDate) ? { ...rule, startDate: dayjs().utc().startOf("day").unix() } : rule));

    if (options && options.fixVersionConflict) {
      await Api.getAsync(calendarRulesURL()).then(
        (response: { rules: CalendarRule[] }) => {
          const { rules: latestRules } = response;
          for (const latest of latestRules) {
            const ruleToUpdate = rules.find((r) => r.id && r.id === latest.id);
            if (!ruleToUpdate) continue;
            ruleToUpdate.version = latest.version;
          }
        },
        (reason: jqXHR) => (statusCode = reason?.status)
      );

      if (statusCode !== 200) {
        throw new ResponseError(errorMessage, statusCode);
      }
    }

    await Api.postAsync(`${calendarRulesBatchURL()}`, { rules }).then(
      (response: { rules: SchoolCalendarRule[] }) => {
        saved = response?.rules;

        if (saved && saved.length > 0) {
          posthog.capture("calendar_rules_saved_in_new_format", {
            saved_rule_count: saved.length,
            calendar_id: saved[0]?.calendarId,
            fix_version_conflict_enabled: !!options?.fixVersionConflict,
          });

          if (saved.length > 1) {
            const { startTime, endTime } = saved[0];
            const allTimesAreEqual = saved.every((r) => r.startTime === startTime && r.endTime === endTime);
            // If all times are not equal means that the user is getting value from the new per day calendar
            if (!allTimesAreEqual) {
              posthog.capture("calendar_rules_saved_in_new_format_with_different_times", {
                saved_rule_count: saved.length,
                calendar_id: saved[0]?.calendarId,
                fix_version_conflict_enabled: !!options?.fixVersionConflict,
              });
            }
          }
        }
      },
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (!saved || statusCode !== 200) {
      throw new ResponseError(errorMessage, statusCode);
    }

    set({ schoolTimeRules: saved });
    return saved;
  },

  delete: async (rule: NonSchoolRule, options?: DeleteOptions): Promise<void> => {
    let statusCode = 200;

    const params = new URLSearchParams();
    if (options && options.recurrenceId) {
      params.append("recurrenceId", String(options.recurrenceId));
      if (options.deleteType) {
        params.append("thisAndFuture", options.deleteType);
      }
    }

    // eslint-disable-next-line
    await Api.deleteAsync(`${calendarRulesURL()}/${rule.id}?${params.toString()}`).then(
      () => ({}),
      (reason: jqXHR) => (statusCode = reason?.status)
    );

    if (statusCode !== 204 && statusCode !== 200) {
      throw new ResponseError("Failed to delete calendar rule", statusCode);
    }

    // cannot delete `school_time` rule hence not handled here

    const rules: NonSchoolRule[] = [];

    for (const r of get().otherRules) {
      if (r.id !== rule.id) {
        rules.push(r);
      }
    }

    set({ otherRules: rules });
  },

  reset: () => {
    set({ schoolTimeRules: [], otherRules: [] });
  },
}));

/**
 * Parses a list of calendar rules, ensuring there is at most one rule per weekday.
 * It transforms legacy rules with multiple days, or with daily recurrence, into
 * the new format of one rule per day. Returns empty array if rules can not be parsed.
 */
export function parseCalendarRules(rules: CalendarRule[]): SchoolCalendarRule[] {
  if (!rules || rules.length === 0) {
    return [];
  }

  // If we don't have "one rule per day" rules, process only the first one
  // to maintain the old behavior.
  if (!oneRulePerDayRules(rules)) {
    const first = rules[0];
    posthog.capture("calendar_rules_migrated_to_new_format", {
      original_rule_count: rules.length,
      migration_type: first?.recurrence?.frequency === Frequency.DAILY ? "daily_to_weekly" : "multi_day_split",
      rule_id: first?.id,
      calendar_id: first?.calendarId,
    });
    rules = [first];
  }

  const parsedRules: SchoolCalendarRule[] = [];

  for (const rule of rules) {
    // This is the case the user selected "All" in the old version
    // of the School Calendar. We just create a WEEKLY rule per day of the week
    // with the original times.
    if (rule.recurrence?.frequency === Frequency.DAILY) {
      for (const day of WEEKDAYS) {
        const newRule = {
          ...rule,
          id: undefined,
          version: 1,
          recurrence: {
            frequency: Frequency.WEEKLY,
            interval: 1,
            days: [day],
          },
        };
        parsedRules.push(newRule);
      }
      continue;
    }

    if (!rule.recurrence?.days || rule.recurrence.days.length === 0) {
      posthog.capture("calendar_rule_parsing_skipped", {
        reason: "missing_recurrence_days",
        rule_id: rule.id,
        calendar_id: rule.calendarId,
      });
      continue;
    }

    // If the original rule only had one day, it's already in the correct format.
    // We push it as-is, preserving its ID and version.
    if (rule.recurrence.days.length === 1) {
      // We assert the type here because the check above guarantees it's safe.
      parsedRules.push(rule as SchoolCalendarRule);
      continue;
    }

    for (const day of rule.recurrence.days) {
      // If the original rule had multiple days, we are creating a new, separate rule for each.
      // It should not have the original ID and its version should be reset.
      const newRule = {
        ...rule,
        id: undefined,
        version: 1,
        recurrence: {
          ...rule.recurrence,
          days: [day], // Set the single day for this new rule
        },
      };
      parsedRules.push(newRule);
    }
  }

  return parsedRules;
}

function oneRulePerDayRules(rules: CalendarRule[]) {
  const first = rules[0];
  const days = first?.recurrence?.days ?? [];
  // If the first rule has multiple days of recurrence we know
  // we are not in the "one rule per day" scenario.
  if (days.length !== 1) return false;

  // The batch update endpoint removes all other school_time rules.
  if (rules.length > 7) return false;

  const seen = new Set<Weekday>();
  for (const rule of rules) {
    const rDays = rule.recurrence?.days ?? [];
    const freq = rule.recurrence?.frequency;
    if (freq !== Frequency.WEEKLY) return false;
    if (rDays.length !== 1) return false;
    const day = rDays[0];
    if (seen.has(day)) return false;
    seen.add(day);
  }
  return true;
}
